import { X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { IoMdArrowBack } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';

const STORAGE_KEY = 'filterKeywords';

const FilterKeywords = () => {
    const navigate = useNavigate();
    const [inputValue, setInputValue] = useState('');
    const [keywords, setKeywords] = useState([]);
    const [hovered, setHovered] = useState(null);

    useEffect(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return;

        try {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed)) {
                setKeywords(parsed);
                return;
            }
        } catch (err) {
            // fall through to set raw value
        }

        setKeywords([stored]);
    }, []);

    const persistKeywords = (values) => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
    };

    const addKeywords = (rawValue) => {
        const entered = rawValue
            .split(',')
            .map((kw) => kw.trim())
            .filter(Boolean);

        if (entered.length === 0) {
            setInputValue('');
            return;
        }

        setKeywords((prev) => {
            const merged = Array.from(new Set([...prev, ...entered]));
            persistKeywords(merged);
            return merged;
        });
        setInputValue('');
    };

    const handleSave = () => addKeywords(inputValue);

    const handleKeyDown = (e) => {
        const shouldCommit = e.key === 'Enter' || e.key === ',';
        if (!shouldCommit) return;

        e.preventDefault();
        addKeywords(inputValue + (e.key === ',' ? '' : ''));
    };

    const handleDelete = (kwToDelete) => {
        const next = keywords.filter((kw) => kw !== kwToDelete);
        setKeywords(next);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    };

    return (
        <div className="bg-white dark:text-white dark:bg-[#1E1D21] mb-5">
            <div className="sticky top-0 z-50">
                <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2">
                    <IoMdArrowBack
                        onClick={() => navigate("/settings/preferences")}
                        className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[22px] text-black cursor-pointer"
                    />
                    <p className="text-center font-bold text-[16px] text-black flex-grow">Filter Keywords</p>
                </div>
            </div>

            <div className="px-3 flex flex-col justify-between h-[calc(80vh-60px)]">
                <div className="flex flex-col pt-5">
                    <p className="text-[14px] text-black dark:text-white font-bold">Manage your filter keywords</p>
                    <p className="text-[11px] font-normal pt-1">
                        Add keywords to filter out content that you don't want to see. Content containing these keywords will be hidden from your feed and search results.
                    </p>
                    <input
                        type="text"
                        placeholder="Enter keywords separated by commas"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="mt-3 text-[14px] w-full p-2 border border-gray-300 rounded-lg focus:border-none focus:outline-none focus:ring-2 focus:ring-[#BDE6FF] dark:bg-[#333333] dark:border-gray-600 dark:placeholder-gray-400"
                    />

                    <div className="mt-4">
                        <p className="text-[13px] font-semibold text-black dark:text-white">Saved keywords</p>
                        {keywords.length > 0 ? (
                            <div className="flex flex-wrap gap-2 mt-2">
                                {keywords.map((kw) => (
                                    <motion.span
                                        key={kw}
                                        layout
                                        animate={{
                                            paddingRight: hovered === kw ? 32 : 12,
                                            scale: hovered === kw ? 1.05 : 1,
                                        }}
                                        transition={{ duration: 0.18, ease: 'easeOut' }}
                                        className="relative inline-flex items-center group px-3 py-1 rounded-full text-[12px] bg-[#E6F5FF] text-black dark:bg-[#333333] dark:text-white border border-[#BDE6FF] dark:border-gray-700 pr-3"
                                        onMouseEnter={() => setHovered(kw)}
                                        onMouseLeave={() => setHovered(null)}
                                    >
                                        {kw}
                                        <AnimatePresence>
                                            {hovered === kw && (
                                                <motion.button
                                                    type="button"
                                                    onClick={() => handleDelete(kw)}
                                                    initial={{ opacity: 0, scale: 0.8, x: 6 }}
                                                    animate={{ opacity: 1, scale: 1, x: 0 }}
                                                    exit={{ opacity: 0, scale: 0.8, x: 6 }}
                                                    transition={{ duration: 0.15, ease: 'easeOut' }}
                                                    className="absolute right-[4px] top-[3px] p-1 rounded-full bg-white/80 dark:bg-[#333333] text-black dark:text-white shadow-sm hover:bg-white hover:text-red-500 transition-colors"
                                                >
                                                    <X size={12} />
                                                </motion.button>
                                            )}
                                        </AnimatePresence>
                                    </motion.span>
                                ))}
                            </div>
                        ) : (
                            <p className="text-[12px] text-gray-500 dark:text-gray-400 mt-2">No keywords saved yet.</p>
                        )}
                    </div>
                </div>

                <button
                    type="button"
                    onClick={handleSave}
                    className="w-full bg-[#BDE6FF] text-black font-bold py-2 rounded-lg hover:bg-[#A0D8FF] transition-colors"
                >
                    Save Keywords
                </button>
            </div>
        </div>
    )
}

export default FilterKeywords